/**
 * KioskQueuePage - Staff display for confirming kiosk orders
 * Shows pending kiosk orders that need staff confirmation before going to kitchen
 * URL: /kiosk-queue
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  CheckCircle, XCircle, Clock, Coffee, ShoppingBag, 
  RefreshCw, Bell, User, CreditCard, AlertCircle,
  ChevronDown, ChevronUp, Printer, UtensilsCrossed
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

export default function KioskQueuePage() {
  const [pendingOrders, setPendingOrders] = useState([]);
  const [confirmedOrders, setConfirmedOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingOrder, setProcessingOrder] = useState(null);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Fetch orders
  const fetchOrders = useCallback(async () => {
    try {
      // Fetch pending kiosk orders
      const [pendingRes, confirmedRes] = await Promise.allSettled([
        api.get('/api/orders?status=pending_confirmation&order_type=kiosk&limit=50'),
        api.get('/api/orders?status=preparing,ready&order_type=kiosk&limit=20')
      ]);

      if (pendingRes.status === 'fulfilled') {
        setPendingOrders(pendingRes.value.data.orders || []);
      }

      if (confirmedRes.status === 'fulfilled') {
        setConfirmedOrders(confirmedRes.value.data.orders || []);
      }
    } catch (err) {
      console.error('Failed to fetch orders:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
    
    // Auto-refresh every 10 seconds if enabled
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchOrders, 10000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [fetchOrders, autoRefresh]);

  // Confirm order - sends to kitchen and triggers auto-print to all 4 stations
  const confirmOrder = async (orderId) => {
    setProcessingOrder(orderId);
    try {
      // Update order status to preparing
      await api.put(`/api/orders/${orderId}/status`, { status: 'preparing' });

      // Trigger auto-print to all 4 stations (POS, Barista, Kitchen, Juicer)
      // This will route items to appropriate printers with "PAID" and "KIOSK ORDER" markers
      try {
        const printRes = await api.post('/api/print-jobs', { 
          order_id: orderId, 
          print_type: 'all',  // Prints customer receipt + all station tickets
          is_reprint: false,
          is_cancelled: false
        });
        
        toast.success(`Order confirmed! Sent to ${printRes.data.jobs?.length || 'all'} printers.`);
      } catch (printErr) {
        console.error('Print error:', printErr);
        toast.success('Order confirmed! Sent to kitchen.');
      }
      
      // Play notification sound
      try {
        const audio = new Audio('/sounds/confirm.mp3');
        audio.volume = 0.5;
        // Audio play may fail if user hasn't interacted with page - this is expected
        audio.play().catch(() => { /* Audio autoplay blocked - expected */ });
      } catch (audioErr) {
        // Audio file not found or not supported - non-critical, continue
        console.debug('Audio feedback unavailable:', audioErr.message);
      }
      
      // Refresh orders
      fetchOrders();
    } catch (err) {
      toast.error('Failed to confirm order');
    }
    setProcessingOrder(null);
  };

  // Reject/Cancel order
  const rejectOrder = async (orderId) => {
    if (!confirm('Are you sure you want to cancel this order? The customer will need to be notified.')) {
      return;
    }
    
    setProcessingOrder(orderId);
    try {
      await api.put(`/api/orders/${orderId}/status`, { status: 'cancelled' });
      toast.success('Order cancelled');
      fetchOrders();
    } catch (err) {
      toast.error('Failed to cancel order');
    }
    setProcessingOrder(null);
  };

  // Print receipt
  const printReceipt = async (orderId) => {
    try {
      await api.post(`/api/receipts/print/${orderId}`, { type: 'customer' });
      toast.success('Receipt sent to printer');
    } catch (err) {
      toast.error('Print failed');
    }
  };

  // Format time ago
  const timeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins === 1) return '1 min ago';
    if (diffMins < 60) return `${diffMins} mins ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours === 1) return '1 hour ago';
    return `${diffHours} hours ago`;
  };

  // Get urgency color based on wait time
  const getUrgencyColor = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMins = Math.floor((now - date) / 60000);
    
    if (diffMins < 2) return 'border-green-500 bg-green-500/10';
    if (diffMins < 5) return 'border-yellow-500 bg-yellow-500/10';
    return 'border-red-500 bg-red-500/10 animate-pulse';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <RefreshCw className="w-12 h-12 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white" data-testid="kiosk-queue-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-800 p-6 sticky top-0 z-10">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center">
              <Bell className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Kiosk Order Queue</h1>
              <p className="text-blue-200">Confirm orders before sending to kitchen</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Pending count badge */}
            {pendingOrders.length > 0 && (
              <div className="bg-red-500 text-white px-4 py-2 rounded-full text-lg font-bold animate-bounce">
                {pendingOrders.length} Pending
              </div>
            )}
            
            {/* Auto-refresh toggle */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm">Auto-refresh</span>
            </label>
            
            <Button
              variant="outline"
              onClick={fetchOrders}
              className="text-white border-white/30 hover:bg-white/10"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Pending Orders Column */}
          <div>
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Clock className="w-6 h-6 text-yellow-400" />
              Awaiting Confirmation ({pendingOrders.length})
            </h2>
            
            {pendingOrders.length === 0 ? (
              <div className="bg-gray-900 rounded-2xl p-12 text-center">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <p className="text-xl text-gray-400">No pending kiosk orders</p>
                <p className="text-gray-500 mt-2">Orders will appear here when customers checkout at the kiosk</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingOrders.map(order => (
                  <div 
                    key={order.id}
                    className={`bg-gray-900 rounded-2xl border-2 overflow-hidden ${getUrgencyColor(order.created_at)}`}
                    data-testid={`pending-order-${order.id}`}
                  >
                    {/* Order Header */}
                    <div className="p-4 border-b border-gray-800">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-16 h-16 rounded-xl bg-blue-600 flex items-center justify-center text-2xl font-bold">
                            #{order.order_number || order.id?.slice(0, 4).toUpperCase()}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-xl font-bold">
                                {order.customer_name || 'Kiosk Guest'}
                              </span>
                              <span className="px-2 py-1 bg-blue-600 text-white text-xs rounded-full">KIOSK</span>
                            </div>
                            <div className="flex items-center gap-4 text-gray-400 text-sm mt-1">
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {timeAgo(order.created_at)}
                              </span>
                              <span className="flex items-center gap-1">
                                <CreditCard className="w-4 h-4 text-green-400" />
                                Paid ${order.total?.toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                        >
                          {expandedOrder === order.id ? (
                            <ChevronUp className="w-5 h-5" />
                          ) : (
                            <ChevronDown className="w-5 h-5" />
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* Order Items (expandable) */}
                    {expandedOrder === order.id && (
                      <div className="p-4 bg-gray-800/50 border-b border-gray-800">
                        <h4 className="text-sm font-medium text-gray-400 mb-3">Order Items:</h4>
                        <div className="space-y-2">
                          {order.items?.map((item, idx) => (
                            <div key={idx} className="flex justify-between text-sm">
                              <div>
                                <span className="font-medium">{item.quantity}x {item.name}</span>
                                {item.selectedSize && (
                                  <span className="text-gray-500 ml-2">({item.selectedSize})</span>
                                )}
                                {item.modifiers?.length > 0 && (
                                  <p className="text-blue-400 text-xs">
                                    {item.modifiers.map(m => m.modifier_name || m.selected_option).join(', ')}
                                  </p>
                                )}
                                {item.removedIngredients?.length > 0 && (
                                  <p className="text-red-400 text-xs">No: {item.removedIngredients.join(', ')}</p>
                                )}
                                {item.specialNotes && (
                                  <p className="text-yellow-400 text-xs">Note: {item.specialNotes}</p>
                                )}
                              </div>
                              <span>${(item.price * item.quantity).toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                        
                        {order.discount > 0 && (
                          <div className="mt-3 pt-3 border-t border-gray-700 text-green-400 text-sm">
                            Discount applied: -${order.discount.toFixed(2)}
                            {order.discount_reason && <span className="text-gray-500"> ({order.discount_reason})</span>}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="p-4 flex gap-3">
                      <Button
                        onClick={() => confirmOrder(order.id)}
                        disabled={processingOrder === order.id}
                        className="flex-1 bg-green-600 hover:bg-green-700 py-6 text-lg"
                        data-testid={`confirm-order-${order.id}`}
                      >
                        {processingOrder === order.id ? (
                          <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                        ) : (
                          <CheckCircle className="w-5 h-5 mr-2" />
                        )}
                        Confirm & Send Order
                      </Button>
                      
                      <Button
                        variant="outline"
                        onClick={() => printReceipt(order.id)}
                        className="border-gray-600 text-white hover:bg-gray-800"
                      >
                        <Printer className="w-5 h-5" />
                      </Button>
                      
                      <Button
                        variant="outline"
                        onClick={() => rejectOrder(order.id)}
                        disabled={processingOrder === order.id}
                        className="border-red-600 text-red-400 hover:bg-red-600/20"
                        data-testid={`reject-order-${order.id}`}
                      >
                        <XCircle className="w-5 h-5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Confirmed Orders Column */}
          <div>
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <UtensilsCrossed className="w-6 h-6 text-green-400" />
              In Preparation ({confirmedOrders.length})
            </h2>
            
            {confirmedOrders.length === 0 ? (
              <div className="bg-gray-900 rounded-2xl p-12 text-center">
                <Coffee className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No orders in preparation</p>
              </div>
            ) : (
              <div className="space-y-3">
                {confirmedOrders.map(order => (
                  <div 
                    key={order.id}
                    className={`bg-gray-900 rounded-xl p-4 border-l-4 ${
                      order.status === 'ready' ? 'border-green-500' : 'border-yellow-500'
                    }`}
                    data-testid={`confirmed-order-${order.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg bg-gray-800 flex items-center justify-center font-bold">
                          #{order.order_number || order.id?.slice(0, 4).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium">{order.customer_name || 'Kiosk Guest'}</p>
                          <p className="text-sm text-gray-400">
                            {order.items?.length} items • ${order.total?.toFixed(2)}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          order.status === 'ready' 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {order.status === 'ready' ? 'Ready' : 'Preparing'}
                        </span>
                        <p className="text-xs text-gray-500 mt-1">{timeAgo(order.created_at)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Audio notification for new orders */}
      {pendingOrders.length > 0 && (
        <audio id="notification-sound" src="/sounds/notification.mp3" preload="auto" />
      )}
    </div>
  );
}
